import React, {FC, useCallback, useState} from 'react';
import styled from '@emotion/styled'
import Modal from '@material-ui/core/Modal';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import {decode} from '@utils/squeezeBase64';
import {MusicPaper} from '@consts/types';
import {Dropzone} from '@common/dropzone';

type UploadModalProps = {
  isOpen: boolean;
  uploadMusicPaper: (musicPaper: MusicPaper) => void;
  closeUploadModal: VoidFunction;
}

const ModalContainer = styled.div`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
  width: 500px;
  height: 500px;
  background-color: #fff;
  padding: 15px;
  outline: none;
`;

const UploadStringTextField = styled(TextField)`
  width: 100%;
  padding-bottom: 15px;
`;

const CustomFormControlLabel = styled(FormControlLabel)`
  .MuiRadio-colorSecondary.Mui-checked {
    color: #4e4e4e;
  }
`;

const CustomButton = styled(Button)`
  &.MuiButton-root {
    background: #fff;
    color: #2f2f2f;
    border: 1px solid #2f2f2f;
    box-shadow: none;
    &:hover {
      background: #fff;
      box-shadow: none;
    }
  }
`;

type UploadType = 'file' | 'dataString';

export const ImportModal: FC<UploadModalProps> = ({ isOpen, uploadMusicPaper, closeUploadModal }) => {
  const [uploadType, setUploadType] = useState<UploadType>('file');
  const [dataString, setDataString] = useState<string>('');

  const handleUploadFile = useCallback((arrayBuffer: ArrayBuffer) => {
    const base64 = new TextDecoder("utf-8").decode(arrayBuffer);
    const musicPaper = decode<MusicPaper>(base64);
    uploadMusicPaper(musicPaper);
  }, [uploadMusicPaper])

  const handleUploadString = useCallback(() => {
    const musicPaper = decode<MusicPaper>(dataString);
    uploadMusicPaper(musicPaper);
  }, [dataString, uploadMusicPaper])

  const handleUploadTypeChange = useCallback((type: string) => {
    setUploadType(type as UploadType)
  }, [setUploadType])

  return (
    <Modal
      open={isOpen}
      onClose={closeUploadModal}
    >
      <ModalContainer>
        <Box sx={{ m: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend"></FormLabel>
            <RadioGroup
              name="uploadType"
              value={uploadType}
              row
              onChange={((_, value) => handleUploadTypeChange(value))}
            >
              <CustomFormControlLabel value="file" control={<Radio />} label="Upload the file" />
              <CustomFormControlLabel value="dataString" control={<Radio />} label="Paste data from the clipboard" />
            </RadioGroup>
          </FormControl>
        </Box>
        {(uploadType === 'file') && (
          <div style={{ margin: '0 15px' }} >
            <Dropzone
              onLoaded={handleUploadFile}
              accept='.json'
            />
          </div>
        )}
        {(uploadType === 'dataString') && (
          <>
            <Box sx={{ m: 2 }}>
              <UploadStringTextField
                label="Paste data from the clipboard"
                multiline
                rows={7}
                placeholder='Paste data from the clipboard'
                value={dataString}
                onChange={(el) => setDataString(el.target.value)}
              />
            </Box>
            <div style={{ margin: '0 15px' }} >
              <CustomButton
                variant="contained"
                fullWidth
                disabled={dataString.length <= 0}
                onClick={handleUploadString}
              >
                Upload
              </CustomButton>
            </div>
          </>
        )}
      </ModalContainer>
    </Modal>
  )
}
